import React, { useMemo, FormHTMLAttributes } from 'react';
import FormControl, { FormControlComponent } from '../FormControl';
import FormControlLabel from '../FormControlLabel';
import FormErrorMessage from '../FormErrorMessage';
import FormGroup from '../FormGroup';
import FormHelpText from '../FormHelpText';
import FormStack from '../FormStack';
import Box from '@/internals/Box';
import useSchemaModel from './hooks/useSchemaModel';
import useFormValidate from './hooks/useFormValidate';
import useFormValue from './hooks/useFormValue';
import useFormRef, { FormInstance, FormImperativeMethods } from './hooks/useFormRef';
import { forwardRef } from '@/internals/utils';
import { Schema, SchemaModel } from 'schema-typed';
import { useEventCallback, useCustom } from '@/internals/hooks';
import { FormValueProvider, FormProvider } from './FormContext';
import type { WithAsProps, CheckTriggerType } from '@/internals/types';
import type { Resolver } from './resolvers';

export interface FormProps<V = Record<string, any>, M = any, E = { [P in keyof V]?: M }>
  extends WithAsProps,
    Omit<FormHTMLAttributes<HTMLFormElement>, 'onChange' | 'onSubmit' | 'onError' | 'onReset'> {
  /**
   * Set the left and right columns of the layout of the elements within the form。
   *
   * @default 'vertical'
   */
  layout?: 'horizontal' | 'vertical' | 'inline';

  /**
   * The fluid property allows the Input 100% of the form to fill the container, valid only in vertical layouts.
   */
  fluid?: boolean;

  /**
   * Current value of the input. Creates a controlled component
   */
  formValue?: V | null;

  /**
   * Initial value
   */
  formDefaultValue?: V | null;

  /**
   * Error message of form
   */
  formError?: E | null;

  /**
   * Trigger the type of form validation.
   *
   * @default 'change'
   */
  checkTrigger?: CheckTriggerType;

  /**
   * SchemaModel object
   *
   * @see https://github.com/rsuite/schema-typed
   */
  model?: Schema;

  /**
   * A resolver function for integrating third-party validation libraries such as
   * Yup, Zod, AJV, Joi, Valibot, etc.
   *
   * When provided, the `resolver` takes precedence over the `model` prop for
   * form-level validation (`check` / `checkAsync`).  Field-level inline `rule`
   * props on `<Form.Control>` components are still respected.
   *
   * The resolver receives the current form values and must return (or resolve to)
   * a `{ errors }` object where each key is a field name and each value is an
   * error message or error object.  An empty `errors` object means the form is valid.
   *
   * **Note:** If the resolver is asynchronous, form-level sync validation
   * (`check()`) will return `false` and log a warning. Use `checkAsync()` or
   * rely on the `onSubmit` callback (which always awaits the resolver).
   *
   * @example
   * ```tsx
   * import * as yup from 'yup';
   *
   * const schema = yup.object({ name: yup.string().email().required() });
   * const resolver = async (formValue) => {
   *   try {
   *     await schema.validate(formValue, { abortEarly: false });
   *     return { errors: {} };
   *   } catch (e) {
   *     const errors = {};
   *     e.inner.forEach(err => { if (err.path) errors[err.path] = err.message; });
   *     return { errors };
   *   }
   * };
   *
   * <Form resolver={resolver} onSubmit={handleSubmit}>…</Form>
   * ```
   */
  resolver?: Resolver<V>;

  /**
   * Make the form readonly
   */
  readOnly?: boolean;

  /**
   * Render the form as plain text
   */
  plaintext?: boolean;

  /**
   * Disable the form
   */
  disabled?: boolean;

  /**
   * The error message comes from context
   */
  errorFromContext?: boolean;

  /**
   * The form data is nested.
   * You may now nest fields with "dot syntax" (e.g. address.city).
   *
   * @default false
   * @version v5.51.0
   * @example
   * ```jsx
   * <Form formValue={{ address: { city: 'Shanghai' } }} nestedField>
   *  <FormControl name="address.city" />
   * </Form>
   * ```
   */
  nestedField?: boolean;

  /**
   * Callback fired when data changing
   */
  onChange?: (formValue: V, event?: React.SyntheticEvent) => void;

  /**
   * Callback fired when error checking
   */
  onError?: (formError: E) => void;

  /**
   * Callback fired when data cheking
   */
  onCheck?: (formError: E) => void;

  /**
   * Callback fired when form submit，only when the form data is validated will trigger
   */
  onSubmit?: (formValue: V | null, event?: React.FormEvent<HTMLFormElement>) => void;

  /**
   * Callback fired when form reset
   */
  onReset?: (formValue: V | null, event?: React.FormEvent<HTMLFormElement>) => void;
}

const defaultSchema = SchemaModel({});

const Subcomponents = {
  Stack: FormStack,
  Control: FormControl as FormControlComponent,
  Label: FormControlLabel,
  ErrorMessage: FormErrorMessage,
  Group: FormGroup,
  Text: FormHelpText,

  /**
   * @deprecated Use `Form.Label` instead
   */
  ControlLabel: FormControlLabel,

  /**
   * @deprecated Use `Form.Text` instead
   */
  HelpText: FormHelpText
};

/**
 * The `Form` component is a form interface for collecting and validating user input.
 * @see https://rsuitejs.com/components/form
 */
const Form = forwardRef<
  'form',
  FormProps & { ref?: React.Ref<FormInstance> },
  typeof Subcomponents
>((props, ref) => {
  const { propsWithDefaults } = useCustom('Form', props);
  const {
    checkTrigger = 'change',
    errorFromContext = true,
    formDefaultValue = {},
    formValue: controlledFormValue,
    formError: controlledFormError,
    nestedField = false,
    fluid,
    layout,
    model: formModel = defaultSchema,
    resolver,
    readOnly,
    plaintext,
    children,
    disabled,
    onSubmit,
    onReset,
    onCheck,
    onError,
    onChange,
    ...rest
  } = propsWithDefaults;

  const { getCombinedModel, pushFieldRule, removeFieldRule } = useSchemaModel(
    formModel,
    nestedField
  );
  const { formValue, onRemoveValue, setFieldValue, resetFormValue } = useFormValue(
    controlledFormValue,
    { formDefaultValue, nestedField }
  );

  const formValidateProps = {
    formValue,
    getCombinedModel,
    onCheck,
    onError,
    nestedField,
    resolver
  };

  const {
    formError,
    onRemoveError,
    check,
    checkAsync,
    checkForField,
    checkFieldForNextValue,
    checkForFieldAsync,
    checkFieldAsyncForNextValue,
    cleanErrors,
    resetErrors,
    cleanErrorForField
  } = useFormValidate(controlledFormError, formValidateProps);

  const submit = useEventCallback((event?: React.FormEvent<HTMLFormElement>) => {
    if (resolver) {
      // When a resolver is provided, always use the async validation path so that
      // both sync and async resolvers are handled correctly.
      checkAsync().then(({ hasError }) => {
        if (!hasError) {
          onSubmit?.(formValue, event);
        }
      });
      return;
    }

    // Check the form before submitting
    if (check()) {
      onSubmit?.(formValue, event);
    }
  });

  const reset = useEventCallback((event?: React.FormEvent<HTMLFormElement>) => {
    resetErrors();
    const resetValue = resetFormValue();
    if (resetValue) {
      onChange?.(resetValue);
    }
    onReset?.(resetValue, event);
  });

  const handleSubmit = useEventCallback((event: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    // Prevent submission when the form is disabled, readOnly, or plaintext
    if (disabled || readOnly || plaintext) {
      return;
    }

    submit(event);
  });

  const handleReset = useEventCallback((event: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    // Prevent reset when the form is disabled, readOnly, or plaintext
    if (disabled || readOnly || plaintext) {
      return;
    }

    reset(event);
  });

  const imperativeMethods: FormImperativeMethods = {
    check,
    checkForField,
    checkAsync,
    checkForFieldAsync,
    cleanErrors,
    cleanErrorForField,
    reset,
    resetErrors,
    submit
  };

  const formRef = useFormRef(ref, { imperativeMethods });

  const removeFieldValue = useEventCallback((name: string) => {
    const formValue = onRemoveValue(name);
    onChange?.(formValue);
  });

  const removeFieldError = useEventCallback((name: string) => {
    onRemoveError(name);
  });

  const onFieldChange = useEventCallback(
    (name: string, value: any, event: React.SyntheticEvent) => {
      const nextFormValue = setFieldValue(name, value);
      onChange?.(nextFormValue, event);
    }
  );

  const formContextValue = {
    errorFromContext,
    checkTrigger,
    plaintext,
    readOnly,
    disabled,
    formError,
    nestedField,
    pushFieldRule,
    removeFieldValue,
    removeFieldError,
    removeFieldRule,
    onFieldChange,
    checkFieldForNextValue,
    checkFieldAsyncForNextValue
  };

  const formChild = useMemo(() => {
    return fluid || layout ? (
      <FormStack fluid={fluid} layout={layout}>
        {children}
      </FormStack>
    ) : (
      children
    );
  }, [fluid, children, layout]);

  return (
    <Box
      as="form"
      data-rs="form"
      data-disabled={disabled}
      data-readonly={readOnly}
      data-plaintext={plaintext}
      ref={formRef}
      onSubmit={handleSubmit}
      onReset={handleReset}
      {...rest}
    >
      <FormProvider value={formContextValue}>
        <FormValueProvider value={formValue}>{formChild}</FormValueProvider>
      </FormProvider>
    </Box>
  );
}, Subcomponents);

Form.displayName = 'Form';

export default Form;
